// Addendum B (B1) — the Listing agent column, shared by the four tables that
// show it (firm-wide Submissions, a buyer's Submissions tab, a buyer's
// Associated listings, the Listings list), and the "off by default" optional
// Agent email / Agent phone columns for the tables with a COLUMNS picker.

import { useCallback, useMemo } from "react";
import type { ListingAgentBriefDto } from "@/api/buyers";
import { MissingValue, type SubTableColumn } from "@/design-system/components/ui";
import { useUserPreference } from "@/hooks/preferences/useUserPreference";

/** "{Agent name} · {Firm}"; a firm contact's label already names the firm
 *  ("Firm contact — {Firm}"). Null with no agent. */
export function listingAgentLine(agent: ListingAgentBriefDto | null | undefined): string | null {
  if (!agent) return null;
  const firm = agent.firmName?.trim();
  return agent.isFirmContact || !firm ? agent.label : `${agent.label} · ${firm}`;
}

/** The agent's phone and email as one short line, or null. */
export function listingAgentContactLine(agent: ListingAgentBriefDto | null | undefined): string | null {
  if (!agent) return null;
  const parts = [agent.phone, agent.email].map((v) => v?.trim()).filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : null;
}

export function ListingAgentCell({
  agent,
  withContact = false,
  testId,
}: {
  agent: ListingAgentBriefDto | null | undefined;
  /** For a table with no COLUMNS picker: phone and email as a second line. */
  withContact?: boolean;
  testId?: string;
}) {
  const line = listingAgentLine(agent);
  if (!line) return <MissingValue data-testid={testId} />;
  const contact = withContact ? listingAgentContactLine(agent) : null;
  return (
    <div className="min-w-0" data-testid={testId}>
      <span className="break-words">{line}</span>
      {contact && <div className="text-caption text-gray-mid break-all">{contact}</div>}
    </div>
  );
}

export const AGENT_COLUMN_KEY = "agent";
export const AGENT_EMAIL_COLUMN_KEY = "agent-email";
export const AGENT_PHONE_COLUMN_KEY = "agent-phone";

/** The two optional columns, OFF until a reader turns them on. */
export const DEFAULT_OFF_AGENT_COLUMNS = [AGENT_EMAIL_COLUMN_KEY, AGENT_PHONE_COLUMN_KEY];

export const AGENT_CHOOSER_COLUMNS = [
  { key: AGENT_COLUMN_KEY, label: "Listing agent" },
  { key: AGENT_EMAIL_COLUMN_KEY, label: "Agent email" },
  { key: AGENT_PHONE_COLUMN_KEY, label: "Agent phone" },
];

/** The Listing agent column (and, for a COLUMNS-picker table, the two optional
 *  contact columns) over any row that carries the agent. */
export function listingAgentColumns<T>(
  agentOf: (row: T) => ListingAgentBriefDto | null | undefined,
  options: { withContactLine?: boolean; withContactColumns?: boolean } = {},
): SubTableColumn<T>[] {
  const columns: SubTableColumn<T>[] = [
    {
      key: AGENT_COLUMN_KEY,
      header: "Listing agent",
      render: (row) => <ListingAgentCell agent={agentOf(row)} withContact={options.withContactLine} />,
      searchText: (row) =>
        [listingAgentLine(agentOf(row)), agentOf(row)?.email, agentOf(row)?.phone].filter(Boolean).join(" "),
      sortAccessor: (row) => listingAgentLine(agentOf(row)) ?? "",
    },
  ];
  if (options.withContactColumns) {
    columns.push(
      {
        key: AGENT_EMAIL_COLUMN_KEY,
        header: "Agent email",
        render: (row) => {
          const email = agentOf(row)?.email;
          return email ? <span className="break-all">{email}</span> : <MissingValue />;
        },
      },
      {
        key: AGENT_PHONE_COLUMN_KEY,
        header: "Agent phone",
        render: (row) => agentOf(row)?.phone || <MissingValue />,
      },
    );
  }
  return columns;
}

/** The hidden set a table renders: the reader's hidden columns plus every
 *  default-off column they have not opted into. */
export function effectiveHiddenColumns(
  hidden: readonly string[],
  defaultOff: readonly string[],
  shown: readonly string[],
): string[] {
  const out = new Set(hidden);
  for (const key of defaultOff) if (!shown.includes(key)) out.add(key);
  return [...out];
}

/** Splits the chooser's next hidden set back into the two stored lists: the
 *  hidden columns (minus the default-off ones) and the opted-in default-off ones. */
export function splitHiddenColumns(
  nextHidden: readonly string[],
  defaultOff: readonly string[],
): { hidden: string[]; shown: string[] } {
  return {
    hidden: nextHidden.filter((k) => !defaultOff.includes(k)),
    shown: defaultOff.filter((k) => !nextHidden.includes(k)),
  };
}

const stringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((k): k is string => typeof k === "string") : [];

/**
 * A COLUMNS picker with default-off columns. The existing per-user preference
 * keeps storing the HIDDEN keys (saved choices stay valid); a second one,
 * `${hiddenPreferenceKey}-shown`, stores the default-off columns the reader has
 * turned ON. No change to `ColumnChooser`.
 */
export function useColumnsWithDefaultOff(hiddenPreferenceKey: string, defaultOff: readonly string[]) {
  const hiddenPref = useUserPreference<string[]>(hiddenPreferenceKey, []);
  const shownPref = useUserPreference<string[]>(`${hiddenPreferenceKey}-shown`, []);
  const { set: setHidden } = hiddenPref;
  const { set: setShown } = shownPref;

  const hiddenKeys = useMemo(
    () => effectiveHiddenColumns(stringList(hiddenPref.value), defaultOff, stringList(shownPref.value)),
    [hiddenPref.value, shownPref.value, defaultOff],
  );
  const onChange = useCallback(
    (nextHidden: string[]) => {
      const { hidden, shown } = splitHiddenColumns(nextHidden, defaultOff);
      setHidden(hidden);
      setShown(shown);
    },
    [defaultOff, setHidden, setShown],
  );
  return { hiddenKeys, onChange };
}
